import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@hero/web-lib';

export interface CounterProps {
  /** Current counter value */
  value?: number;
  /** Callback when value changes */
  onChange: (value: number) => void;
  /** Minimum value (default: 0) */
  min?: number;
  /** Maximum value */
  max?: number;
  /** Disabled state */
  disabled?: boolean;
  /** Optional className */
  className?: string;
  /** Optional RTL support */
  isRTL?: boolean;
}

/**
 * Counter - A reusable counter component with increment/decrement buttons.
 * Displays the current value and provides - and + buttons to change it.
 *
 * @example
 * ```tsx
 * <Counter
 *   value={quantity}
 *   onChange={setQuantity}
 *   min={0}
 *   max={10}
 *   disabled={false}
 * />
 * ```
 */
export function Counter({
  value = 0,
  onChange,
  min = 0,
  max,
  disabled = false,
  className,
  isRTL,
}: CounterProps) {
  const { i18n } = useTranslation();
  const rtl = isRTL ?? i18n.language === 'ar';

  const handleIncrement = () => {
    if (max === undefined || value < max) {
      onChange(value + 1);
    }
  };

  const handleDecrement = () => {
    if (value > min) {
      onChange(value - 1);
    }
  };

  const isDecrementDisabled = disabled || value <= min;
  const isIncrementDisabled = disabled || (max !== undefined && value >= max);

  return (
    <div className={cn('flex items-center gap-4', rtl && 'flex-row-reverse', className)}>
      <button
        type="button"
        className="w-[36px] h-[36px] text-[24px] flex items-center justify-center border-2 border-gray-400 rounded-full p2 hover:border-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
        onClick={handleDecrement}
        disabled={isDecrementDisabled}>
        -
      </button>
      <span className="text-[20px] font-[700] leading-[28px] text-text min-w-[30px] text-center">
        {value}
      </span>
      <button
        type="button"
        className="w-[36px] h-[36px] text-[24px] flex items-center justify-center border-2 border-primary bg-primary text-white rounded-full p2 hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
        onClick={handleIncrement}
        disabled={isIncrementDisabled}>
        +
      </button>
    </div>
  );
}
